import { useSelector } from "react-redux";

import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { Address, WarehouseID } from "@models/Address";
import client, { RequestParams } from "@services/api";
import { PaginatedQueryOptions, buildPaginatedQuery } from "@utils/reactQuery";
import useRoleIs from "@utils/useRoleIs";

type AddressPayload = {
  id: string;
  type: "address" | "warehouse";
  city: string;
  name: string;
  streetAddress1: string;
  streetAddress2?: string | null;
  defaultAttn?: string | null;
  externalId?: string | null;
  phoneNumber: string;
  region?: string | null;
  warehouse?: WarehouseID;
  zip: string;
  countryId: string | number;
  stateId?: string | number | null;
};

type NewAddressPayload = Omit<AddressPayload, "id"> & {
  territoryId: number;
  userId: string | number;
};

export const addressesKeyFactory = createQueryKeys("addresses", {
  detail: (id: string) => ({
    queryKey: [id],
    queryFn: () =>
      client.get<Address>(`addresses/${id}`).then((res) => res.data),
  }),
  paginated: (params) => ({
    queryKey: [params],
    queryFn: () =>
      client.get<Address[]>("addresses", {
        params,
      }),
  }),
});

export const useAddressQuery = (id: string | null | undefined) => {
  return useQuery({
    ...addressesKeyFactory.detail(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
};

export const usePaginatedAddressesQuery = buildPaginatedQuery<Address>(
  addressesKeyFactory.paginated,
  {
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  }
);

export const usePaginatedAddressBookQuery = (
  params?: RequestParams,
  options?: PaginatedQueryOptions<Address>
) => {
  const {
    id: userId,
    currentTerritoryId,
    organization: { addressBookType },
  } = useSelector((state: any) => state.currentUser);

  const roleIs = useRoleIs();
  const isAdmin = roleIs("super", "admin");

  const userIdFilter = !isAdmin && addressBookType === "user" ? [userId] : null;

  return usePaginatedAddressesQuery(
    {
      ...params,
      filter: {
        territoryId: currentTerritoryId,
        userIds: userIdFilter,
        ...params?.filter,
      },
    },
    options
  );
};

/* Mutations */

const buildAddressPayload = (
  data: NewAddressPayload | Omit<AddressPayload, "id">
) => ({
  __type: "address",
  ...data,
});

export const useCreateAddressMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: NewAddressPayload) =>
      client
        .post<Address>(`addresses`, buildAddressPayload(data))
        .then((res) => res.data),
    onSuccess: (address) => {
      queryClient.invalidateQueries({
        queryKey: addressesKeyFactory.paginated._def,
      });
      queryClient.setQueryData<Address>(
        addressesKeyFactory.detail(address.id).queryKey,
        address
      );
    },
  });
};

export const useUpdateAddressMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: AddressPayload) =>
      client
        .update<Address>(`addresses/${id}`, buildAddressPayload(data))
        .then((res) => res.data),
    onSuccess: (address) => {
      queryClient.invalidateQueries({
        queryKey: addressesKeyFactory.paginated._def,
      });
      queryClient.setQueryData<Address>(
        addressesKeyFactory.detail(address.id).queryKey,
        address
      );
    },
  });
};

type ImportAddressPayload = {
  city: string;
  name: string;
  streetAddress1: string;
  streetAddress2?: string | null;
  defaultAttn?: string | null;
  externalId?: string | null;
  phoneNumber: string;
  region?: string | null;
  warehouse?: WarehouseID;
  zip: string;
  countryId: string | number;
  stateId?: string | number | null;

  territoryList: string[];
  emailList: string[];
};

export const useCreateAddressImportMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ImportAddressPayload) =>
      client.post<Address>(`addresses/import`, {
        ...data,
        __type: "address",
        type: "address",
        relationshipNames: ["country", "state", "user"],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: addressesKeyFactory.paginated._def,
      });
    },
  });
};
